import { useSyncExternalStore } from 'react'
import { getSupabaseIdentity, useSupabaseToken } from '../hooks/useSupabaseToken'
import { collectionOwnerOf } from './collectionOwner'
import { pointerRemovalInProgress, pointerWriteSeq } from './collectionPointerWriter'
import { collectionLockUnavailable, isCollectionLeader, useCollectionLockUnavailable, useIsCollectionLeader } from './collectionLeader'
import { readCollectionScope } from './collectionScope'

// Facts about collection writes, shared by every surface that runs one, by
// CollectionSync and by the Remove control.
//
//  - IN FLIGHT: how many user-started import/refresh writes are running, from
//    the request THROUGH the awaited local save and cloud upsert. Remove is
//    disabled while any is.
//  - CLEAR GENERATION: bumped synchronously by every SUCCESSFUL clear (Remove
//    after its verified delete, account deletion, and a clear another tab made,
//    seen through the storage event). A write captures it when it starts and
//    saves nothing if it moved: an import that finishes after the collection
//    was removed must not bring it back.
//  - IDENTITY: the signed-in user a write started under, from the ONE shared
//    auth store (useSupabaseToken). It must be KNOWN to start: while that store
//    is still loading, nothing starts (fail closed; the surfaces are disabled
//    meanwhile). A definitively signed-out user starts anonymously (null). The
//    captured user is what the request is sent as (writeAuthFor refuses any
//    other session), and a result is committed only if the live user is still
//    that one.
//    "User" here is the collection OWNER (collectionOwnerOf): an anonymous
//    Supabase session is a guest, exactly like signed out.
//  - LEADER: only the writer tab (collectionLeader) starts any collection
//    write, import, sync and per-copy alike. Reads stay allowed everywhere.
//  - POINTER SEQUENCE: collectionPointerWriter's sequence number at the start,
//    so a cloud write from an operation that predates a successful Remove is
//    rejected by the writer's tombstone.
//
// Its own module (not useCollection) so page tests that mock useCollection
// keep the real fence.

let clearGeneration = 0
let inFlight = 0
// The pointer lease (see "Pointer movers" below): held by at most one mover.
let pointerLeaseHeld = false
// Moves when the collection a per-copy write may target changes IDENTITY:
// another id, the same id re-imported (its contents replaced), a reload from
// storage that changed the pointer or epoch, or a clear. NOT on a canonical
// owned-names commit (CollectionSync's sync after a per-copy write), which
// moves the content epoch but leaves the collection the same one.
let identityGeneration = 0
const listeners = new Set<() => void>()

function notify() {
  for (const l of listeners) l()
}

/** Called by every successful clear, BEFORE local state is cleared. */
export function bumpCollectionClearGeneration(): void {
  clearGeneration += 1
}

export function collectionClearGeneration(): number {
  return clearGeneration
}

/**
 * The live collection OWNER if KNOWN: a permanent user's id, or null for a
 * guest (signed out, or an anonymous session); undefined = unknown.
 */
export function knownUserId(): string | null | undefined {
  return collectionOwnerOf(getSupabaseIdentity())
}

export interface CollectionWriteCapture {
  /** The user this operation runs as (null = anonymous). Never unknown. */
  userId: string | null
  /** collectionPointerWriter sequence at the start (stamp it on cloud writes). */
  pointerSeq: number
  /** True once a clear landed, or the live user is no longer `userId`. */
  fenced: () => boolean
}

/**
 * Capture identity, clear generation and pointer sequence NOW, without
 * marking anything in flight (CollectionSync's background passes use this,
 * so a routine probe never disables Remove). Null when the identity is not
 * known yet: the caller must not start.
 */
export function captureCollectionWrite(): CollectionWriteCapture | null {
  // Only the writer tab writes collections (collectionLeader); every other
  // tab is read-only, so nothing starts there.
  if (!isCollectionLeader()) return null
  const userId = knownUserId()
  if (userId === undefined) return null
  const startedAt = clearGeneration
  return {
    userId,
    pointerSeq: pointerWriteSeq(),
    // An identity that became unknown also fences: fail closed.
    fenced: () => clearGeneration !== startedAt || knownUserId() !== userId,
  }
}

/**
 * Why beginCollectionWrite would refuse right now, in the order it checks,
 * so a refused user action can say why instead of doing nothing:
 *   'busy'        - a per-copy write or a pointer mover is running
 *   'unavailable' - Web Locks refused this tab (read-only, fail closed)
 *   'follower'    - another tab is the collection writer
 *   'auth'        - who is signed in is not known yet
 *   null          - a write may start
 */
export type CollectionWriteRefusal = 'busy' | 'unavailable' | 'follower' | 'auth'
export function collectionWriteRefusal(): CollectionWriteRefusal | null {
  // eslint-disable-next-line i18next/no-literal-string -- refusal code
  if (collectionPointerBusy()) return 'busy'
  if (!isCollectionLeader()) {
    // eslint-disable-next-line i18next/no-literal-string -- refusal code
    return collectionLockUnavailable() ? 'unavailable' : 'follower'
  }
  // eslint-disable-next-line i18next/no-literal-string -- refusal code
  if (knownUserId() === undefined) return 'auth'
  return null
}

/**
 * Start one user-initiated collection write: captured as above AND in flight
 * until `end()` (idempotent; call it in a finally AFTER the awaited save).
 * Null when the identity is not known yet (nothing is marked in flight then),
 * or while ANY per-copy write is running (including one being reconciled):
 * an import, refresh, claim or Switch back may move the pointer, and a
 * per-copy request already in the air targets the collection it started on.
 * The reverse direction is tryBeginCopiesWrite's: it refuses while this
 * counter is non-zero.
 */
export function beginCollectionWrite(): (CollectionWriteCapture & { end: () => void }) | null {
  if (collectionPointerBusy()) return null
  const capture = captureCollectionWrite()
  if (!capture) return null
  inFlight += 1
  notify()
  let ended = false
  return {
    ...capture,
    end: () => {
      if (ended) return
      ended = true
      inFlight -= 1
      notify()
    },
  }
}

// --- Per-copy writes (plan 2026-10-16 C3) ----------------------------------
//
// A per-copy write (add, patch, delete, bulk on /collections/{id}/copies)
// never touches the saved_collections pointer, but it must still never race a
// Remove: a PATCH that lands after the collection was removed edits a row the
// user just deleted, and an optimistic update applied after it would show
// cards that are gone. So:
//  - tryBeginCopiesWrite refuses, atomically (no await between the checks and
//    the registration), while a removal is queued or running, when the live
//    user is not the one the write is for, while identity is unknown, or
//    while a user-started collection write (import, refresh, claim, Switch
//    back) is in flight; beginCollectionWrite refuses the other way round
//    while any per-copy write runs, so the two never overlap;
//  - `fenced()` also trips when the live pointer moves to another collection
//    while the write runs;
//  - the capture is taken BEFORE any optimistic update, and `fenced()` is
//    re-checked after every await and before a response is applied;
//  - a removal calls settleCopiesWritesForRemoval() before it deletes: every
//    in-flight copies write is aborted through its AbortController and the
//    removal waits until each has ended (bounded, so a stuck write can never
//    wedge Remove).
//
// Copies writes are NOT counted in `inFlight` above: that counter disables the
// Remove button, and a qty tap must not do that. Remove aborts them instead.

export interface CopiesWriteCapture {
  /** The signed-in user the write runs as. Never null: per-copy is owner-only. */
  userId: string
  /** Abort signal for the request; aborted when a removal starts. */
  signal: AbortSignal
  /** True once a clear landed, a removal started, or the live user changed. */
  fenced: () => boolean
  /**
   * The same, minus this write's own abort: a write the server CONFIRMED
   * after Remove aborted (and gave up on) it is still the user's change.
   */
  fencedIgnoringAbort: () => boolean
  /** Idempotent; call in a finally. */
  end: () => void
}

type CopiesWriteEntry = {
  controller: AbortController
  done: Promise<void>
  resolve: () => void
  userId: string
  /** The collection the write targets (absent: an older caller, any collection). */
  collectionId?: string
}
const copiesWrites = new Set<CopiesWriteEntry>()
// Observers of the per-copy write registry (useCopiesWriteInFlight): the
// search card sheet refuses to start a single-card add while another write
// to the same owner's collection is running (plan card-sheet-actions, 3).
const copiesListeners = new Set<() => void>()
function notifyCopies() {
  for (const l of copiesListeners) l()
}

/**
 * The SESSION a per-copy operation belongs to: the live owner, the collection
 * pointer and its identity, and the clear generation, as they are now.
 * `live()` turns false for good once the owner changes (sign-out, another
 * account), the collection is cleared or replaced, or the same id changed
 * identity. Unlike tryBeginCopiesWrite it does not require that a write
 * could start NOW (a follower tab, a lease): it answers "is this still the
 * session the operation was made in". Dead at once when `expectedUserId` is
 * not the live owner or `collectionId` not the live pointer.
 */
export function captureCopiesSession(expectedUserId: string, collectionId: string): { live: () => boolean } {
  const userId = knownUserId()
  if (userId === undefined || userId === null || userId !== expectedUserId) return { live: () => false }
  if (readCollectionScope().collectionId !== collectionId) return { live: () => false }
  const startedAt = clearGeneration
  const identityAtStart = identityGeneration
  return {
    live: () => clearGeneration === startedAt
      && knownUserId() === userId
      && readCollectionScope().collectionId === collectionId
      && identityGeneration === identityAtStart,
  }
}

/**
 * The SAME gates as tryBeginCopiesWrite (leader tab, no removal, no
 * user-started collection write or pointer lease, the live user is
 * `expectedUserId`, the live pointer is `collectionId`), checked WITHOUT
 * registering a write. For local-only work tied to a write that has not
 * started yet (a queued per-copy change shown at once): null when such a
 * write could not start now; else `live()`, false from the moment a clear, a
 * removal, an identity change or a pointer move would fence it.
 */
export function captureCopiesWriteContext(expectedUserId: string, collectionId: string): { live: () => boolean } | null {
  if (!isCollectionLeader()) return null
  if (pointerRemovalInProgress()) return null
  if (inFlight > 0 || pointerLeaseHeld) return null
  const userId = knownUserId()
  if (userId === undefined || userId === null || userId !== expectedUserId) return null
  const pointerAtStart = readCollectionScope().collectionId
  if (collectionId !== pointerAtStart) return null
  const startedAt = clearGeneration
  const identityAtStart = identityGeneration
  return {
    live: () => clearGeneration === startedAt
      && knownUserId() === userId
      && isCollectionLeader()
      && !pointerRemovalInProgress()
      && readCollectionScope().collectionId === pointerAtStart
      && identityGeneration === identityAtStart,
  }
}

/**
 * Start one per-copy write for `expectedUserId`, or null when it may not start
 * (a removal is in progress, identity is unknown, or a different user is
 * signed in). Synchronous on purpose: nothing can slip between the checks and
 * the registration that Remove waits on.
 */
export function tryBeginCopiesWrite(expectedUserId: string, collectionId?: string): CopiesWriteCapture | null {
  // Follower tabs never write: the per-copy controls there are disabled with
  // the "open in another tab" notice, and this is the backstop.
  if (!isCollectionLeader()) return null
  if (pointerRemovalInProgress()) return null
  // Mutually exclusive with user-started collection writes (import, refresh,
  // claim, Switch back): one of those may move the pointer under this write.
  if (inFlight > 0 || pointerLeaseHeld) return null
  const userId = knownUserId()
  if (userId === undefined || userId === null || userId !== expectedUserId) return null
  // Bound to the collection it was asked for: a stale caller for c1 while the
  // live pointer is c2 (or none) never registers.
  const pointerAtStart = readCollectionScope().collectionId
  if (collectionId !== undefined && collectionId !== pointerAtStart) return null
  const startedAt = clearGeneration
  // Captured together with the pointer, synchronously: the write is fenced
  // once the live pointer is another id OR the same id changed identity in
  // between (c1 -> c2 -> c1, or c1 re-imported in place).
  const identityAtStart = identityGeneration
  const pointerMoved = () => readCollectionScope().collectionId !== pointerAtStart
    || identityGeneration !== identityAtStart
  const controller = new AbortController()
  let resolve!: () => void
  const done = new Promise<void>(r => { resolve = r })
  const entry: CopiesWriteEntry = { controller, done, resolve, userId, collectionId }
  copiesWrites.add(entry)
  notifyCopies()
  let ended = false
  return {
    userId,
    signal: controller.signal,
    fenced: () => clearGeneration !== startedAt
      || knownUserId() !== userId
      || pointerRemovalInProgress()
      || pointerMoved()
      || controller.signal.aborted,
    fencedIgnoringAbort: () => clearGeneration !== startedAt
      || knownUserId() !== userId
      || pointerRemovalInProgress()
      || pointerMoved(),
    end: () => {
      if (ended) return
      ended = true
      copiesWrites.delete(entry)
      entry.resolve()
      notifyCopies()
    },
  }
}

/** How many per-copy writes are running (tests, diagnostics). */
export function copiesWritesInFlight(): number {
  return copiesWrites.size
}

/**
 * True while any per-copy write (add, patch, delete, bulk, undo, including
 * one still being reconciled) runs for `userId`'s collection `collectionId`.
 * A write registered without a collection counts for every collection.
 */
export function copiesWriteActive(userId: string | null, collectionId: string | null): boolean {
  if (!userId || !collectionId) return false
  for (const e of copiesWrites) {
    if (e.userId === userId && (e.collectionId === undefined || e.collectionId === collectionId)) return true
  }
  return false
}

/** copiesWriteActive for React: re-renders when the registry changes. */
export function useCopiesWriteInFlight(userId: string | null, collectionId: string | null): boolean {
  return useSyncExternalStore(
    cb => { copiesListeners.add(cb); return () => { copiesListeners.delete(cb) } },
    () => copiesWriteActive(userId, collectionId),
    () => false,
  )
}

/**
 * Called by Remove before it deletes anything: abort every in-flight per-copy
 * write and wait for each to end, at most `timeoutMs`. `timed_out` means a
 * write is still running: Remove then deletes nothing and fails retryably
 * (a stuck write can never wedge Remove, and never races its delete).
 */
export type CopiesSettle = 'settled' | 'timed_out'

export async function settleCopiesWritesForRemoval(timeoutMs = 5_000): Promise<CopiesSettle> {
  if (copiesWrites.size === 0) return SETTLED
  const pending = [...copiesWrites]
  for (const e of pending) e.controller.abort()
  let timer: ReturnType<typeof setTimeout> | undefined
  const outcome = await Promise.race([
    Promise.all(pending.map(e => e.done)).then((): CopiesSettle => SETTLED),
    new Promise<CopiesSettle>(r => { timer = setTimeout(() => r(TIMED_OUT), timeoutMs) }),
  ])
  if (timer !== undefined) clearTimeout(timer)
  // A write that did not end in time may still land: the caller must NOT
  // treat this as settled (Remove refuses to delete and reports a retryable
  // failure instead).
  return outcome
}

const SETTLED: CopiesSettle = 'settled'
const TIMED_OUT: CopiesSettle = 'timed_out'

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

function getInFlight(): boolean {
  return inFlight > 0
}

/** Non-hook read of the same (an add re-checks it right before it sends). */
export function collectionWriteInFlightNow(): boolean {
  return inFlight > 0
}

/** True while any collection import/refresh write is running. */
export function useCollectionWriteInFlight(): boolean {
  return useSyncExternalStore(subscribe, getInFlight, () => false)
}

/**
 * Why collection writes cannot start in this tab right now, for the controls:
 *   'auth'     - who is signed in is not known yet (fail closed)
 *   'follower' - another tab is the collection writer (read-only here)
 *   'unavailable' - Web Locks refused this tab (read-only here, fail closed)
 *   null       - writes may start
 */
export function useCollectionWriteBlock(): 'auth' | 'follower' | 'unavailable' | null {
  const identity = useSupabaseToken()
  const leader = useIsCollectionLeader()
  const unavailable = useCollectionLockUnavailable()
  // eslint-disable-next-line i18next/no-literal-string -- block code
  if (unavailable) return 'unavailable'
  // eslint-disable-next-line i18next/no-literal-string -- block code
  if (!leader) return 'follower'
  // eslint-disable-next-line i18next/no-literal-string -- block code
  if (identity.loading) return 'auth'
  return null
}

// --- Pointer movers (Codex r2) ---------------------------------------------
//
// Everything that moves the account's collection pointer outside an import
// (the sign-in reconcile's claim, mint and adopt phases, the legacy
// migration, "Use account collection") holds ONE exclusive lease while it
// does. The lease, user-started collection writes (beginCollectionWrite) and
// per-copy writes (tryBeginCopiesWrite) are mutually exclusive: none starts
// while another kind is running, and only one lease exists at a time. A mover
// that has to wait (a per-copy write is reconciling) waits for the registry
// to clear, revalidates, and releases the lease while it waits on a user.

export interface PointerLease {
  /** Idempotent. */
  release: () => void
  /** False once released, expired (maxHoldMs) or aborted (the caller's signal). */
  live: () => boolean
  /** Aborts when the lease ends for any reason: held work must stop. */
  signal: AbortSignal
}

/**
 * Longest a lease may be HELD. Holding is hard-bounded, not just waiting: a
 * hung request under the lease can never keep imports, per-copy writes and
 * Remove out for longer than this. On expiry the lease is released and its
 * signal aborted; the holder's continuations must check `live()` before any
 * mutation (collectionReconcile's held phases do).
 */
export const POINTER_LEASE_MAX_HOLD_MS = 45_000

/**
 * The lease now, or null while anything else holds the collection: another
 * lease, an import or Switch back, a per-copy write, or a Remove (symmetric:
 * Remove refuses while a lease is held, useCollection.clearCollection).
 * `signal` (an unmount, an account change) releases it at once.
 */
export function tryAcquirePointerLease(
  opts: { maxHoldMs?: number; signal?: AbortSignal } = {},
): PointerLease | null {
  if (pointerLeaseHeld || inFlight > 0 || copiesWrites.size > 0 || pointerRemovalInProgress()) return null
  if (opts.signal?.aborted) return null
  pointerLeaseHeld = true
  notify()
  notifyCopies()
  const controller = new AbortController()
  let released = false
  const release = () => {
    if (released) return
    released = true
    // Declared below; release only ever runs after that (timer or signal).
    clearTimeout(expiry)
    // eslint-disable-next-line i18next/no-literal-string -- DOM event name
    opts.signal?.removeEventListener('abort', release)
    pointerLeaseHeld = false
    controller.abort()
    notify()
    notifyCopies()
  }
  const expiry = setTimeout(release, opts.maxHoldMs ?? POINTER_LEASE_MAX_HOLD_MS)
  // eslint-disable-next-line i18next/no-literal-string -- DOM event name
  opts.signal?.addEventListener('abort', release, { once: true })
  return { release, live: () => !released, signal: controller.signal }
}

/** Longest a mover waits for the collection to be free (the reconcile deadline plus margin). */
export const POINTER_LEASE_WAIT_MS = 90_000
const LEASE_POLL_MS = 1_000

/**
 * The lease, waiting while something else holds the collection. Null when
 * `stillWanted()` turns false (checked on every change and once a second) or
 * after `timeoutMs`: the caller then moves nothing (the next load retries).
 */
export function acquirePointerLease(
  stillWanted: () => boolean, timeoutMs = POINTER_LEASE_WAIT_MS,
  opts: { maxHoldMs?: number; signal?: AbortSignal } = {},
): Promise<PointerLease | null> {
  return new Promise((resolve) => {
    let done = false
    // Assigned once, AFTER the first attempt (which may finish at once): a
    // const there would be read by finish() before its initialization.
    // eslint-disable-next-line prefer-const
    let timer: ReturnType<typeof setTimeout> | undefined
    // eslint-disable-next-line prefer-const
    let poll: ReturnType<typeof setInterval> | undefined
    const finish = (v: PointerLease | null) => {
      if (done) return
      done = true
      listeners.delete(attempt)
      copiesListeners.delete(attempt)
      if (timer !== undefined) clearTimeout(timer)
      if (poll !== undefined) clearInterval(poll)
      resolve(v)
    }
    function attempt() {
      if (done) return
      if (!stillWanted() || opts.signal?.aborted) { finish(null); return }
      const lease = tryAcquirePointerLease(opts)
      if (lease) finish(lease)
    }
    attempt()
    if (done) return
    listeners.add(attempt)
    copiesListeners.add(attempt)
    timer = setTimeout(() => finish(null), timeoutMs)
    poll = setInterval(attempt, LEASE_POLL_MS)
  })
}

/** A pointer mover holds the lease right now. */
export function pointerLeaseActive(): boolean {
  return pointerLeaseHeld
}

/** The same, for React (per-copy surfaces show "still syncing" meanwhile). */
export function usePointerLeaseActive(): boolean {
  return useSyncExternalStore(subscribe, pointerLeaseActive, () => false)
}

/**
 * Something keeps imports, refreshes, claims, Switch back and "Use account
 * collection" out right now: a per-copy write or a pointer mover. Exactly
 * what beginCollectionWrite and tryAcquirePointerLease refuse on (besides an
 * import already running).
 */
export function collectionPointerBusy(): boolean {
  return copiesWrites.size > 0 || pointerLeaseHeld
}

/**
 * Every change of the write registry (imports, per-copy writes, the lease).
 * A deferred mover (CollectionSync's setup) retries when the fence clears.
 */
export function subscribeCollectionWrites(cb: () => void): () => void {
  listeners.add(cb)
  copiesListeners.add(cb)
  return () => { listeners.delete(cb); copiesListeners.delete(cb) }
}

/** Nothing holds the collection: no import, per-copy write, lease or Remove. */
export function collectionWritesIdle(): boolean {
  return inFlight === 0 && copiesWrites.size === 0 && !pointerLeaseHeld && !pointerRemovalInProgress()
}

/** The same, for React. */
export function useCollectionPointerBusy(): boolean {
  return useSyncExternalStore(
    cb => {
      copiesListeners.add(cb)
      listeners.add(cb)
      return () => { copiesListeners.delete(cb); listeners.delete(cb) }
    },
    collectionPointerBusy,
    () => false,
  )
}

/**
 * The collection a per-copy write may target changed identity (useCollection
 * calls this on a new pointer id, an import that replaced the contents, a
 * reload from storage that moved the pointer or epoch, and a clear). Fences
 * every per-copy write in flight.
 */
export function noteCollectionIdentityChange(): void {
  identityGeneration += 1
}

// --- This tab's own per-copy changes (Codex r3, finding 1) -----------------
//
// The owned-names version (the server content hash) moves on OUR per-copy
// writes as well as on a re-import made elsewhere. A canonical sync that sees
// a new version for the same collection id is "ours" only when it can be
// explained by one of this tab's CONFIRMED per-copy writes that happened
// before the sync started reading; otherwise it is a remote replacement and
// moves the collection's identity (fencing every per-copy write in flight).
// Each own change is consumed by the first canonical commit that read after
// it, so it can never explain a later remote change.

let ownChangeSeq = 0
const pendingOwnChanges = new Map<string, number[]>()

/** A per-copy write to `collectionId` CONFIRMED (useCopyMutations). */
export function noteOwnCopiesChange(collectionId: string): void {
  ownChangeSeq += 1
  const list = pendingOwnChanges.get(collectionId) ?? []
  list.push(ownChangeSeq)
  pendingOwnChanges.set(collectionId, list)
}

/** Taken by a canonical sync BEFORE it reads the server's names. */
export function ownCopiesChangeStamp(): number {
  return ownChangeSeq
}

/**
 * A canonical commit for `collectionId` that read after `stamp`: consume the
 * own changes it observed (confirmed at or before `stamp`). True when there
 * was at least one, i.e. a version change it saw may be this tab's own.
 */
export function consumeOwnCopiesChanges(collectionId: string, stamp: number): boolean {
  const list = pendingOwnChanges.get(collectionId)
  if (!list?.length) return false
  const seen = list.filter(n => n <= stamp)
  const rest = list.filter(n => n > stamp)
  if (rest.length) pendingOwnChanges.set(collectionId, rest)
  else pendingOwnChanges.delete(collectionId)
  return seen.length > 0
}

/** Test seam. */
export function __resetCollectionWriteFence(): void {
  ownChangeSeq = 0
  pendingOwnChanges.clear()
  clearGeneration = 0
  pointerLeaseHeld = false
  identityGeneration = 0
  inFlight = 0
  copiesWrites.clear()
  notify()
  notifyCopies()
}
